import * as Sentry from '@sentry/vue'
import CrossDomainHttpClient from '@/services/crossDomainHttp'
import Configs from '@/config'
import PostMessageSender from '@/services/postMessageSender'
import AIChatbotReadinessService from '@/services/aiChatbotReadiness'
import { waitForCloudImportReady, waitForImportJob } from '@/services/importPolling'
import EventBus from '@/eventBus'
import utils from '@/util'
import OpenAISocketHandler from '@/utils/open-ai/OpenAISocketHandler'
import router from '@/router'
import i18nPresenter from '@/i18nPresenter'
import {
  IMPORT_OPTIONS, IMPORT_ACTION_OPTIONS, UPLOAD_FILE_SUCCESSFUL,
  UPLOAD_FILE_FAILED, IMPORT_V2_SUCCESSFUL, IMPORT_V2_FAILED,
  FEATURE_IMPORT_V2,
} from '@/constant/eventActions/ImportEventActions'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

const { externalApis } = Configs
const IFRAME_CONTENT_MAX_WAIT_TIME = 20000
const IFRAME_POST_READINESS_DELAY_MS = 2000
const IS_FEATURE_ENABLED = 'featureToggles/isFeatureEnabled'

const CLOUD_IMPORT_POLL_INTERVAL_MS = 2000
const CLOUD_IMPORT_POLL_MAX_ATTEMPTS = 150
const IMPORT_JOB_POLL_INTERVAL_MS = 2000
const IMPORT_JOB_POLL_MAX_ATTEMPTS = 600

/**
 * Generates authentication headers for API requests
 * @returns {Object} Headers object with Authorization token if available
 */
const getAuthHeaders = () => {
  // Simple cookie getter for ahaToken
  const getCookie = (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    return parts.length === 2 ? parts.pop().split(';').shift() : null
  }

  const token = getCookie('ahaToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const updateCloudImportMetadata = (commit, state, patch) => {
  commit('SET_AI_CHATBOT_UPLOADED_FILE', {
    ...state.aiChatbotUploadedFile,
    file_metadata: {
      ...state.aiChatbotUploadedFile?.file_metadata,
      ...patch,
    },
  })
}

const handleCloudImportFailed = (commit, state, errorMessage) => {
  commit('SET_AI_IMPORT_SLIDES_ERROR', errorMessage)
  updateCloudImportMetadata(commit, state, { import_status: 'failed' })
}

const trackUploadFileSuccessful = (file, extra = {}) => {
  const eventAction = UPLOAD_FILE_SUCCESSFUL
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
    file_name: file?.name,
    ...extra,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackUploadFileFailed = (file, error, extra = {}) => {
  const eventAction = UPLOAD_FILE_FAILED
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
    file_name: file?.name,
    ...(error?.reason ? { reason: error.reason } : {}),
    error_message: error?.body?.message || error?.message || 'Could not upload this file. Please try again.',
    ...extra,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const cloudImportFile = (state) => ({ name: state.aiChatbotUploadedFile?.file_metadata?.original_filename })
const cloudImportExtra = (state) => ({
  source: 'cloud',
  provider: state.aiChatbotUploadedFile?.import_provider,
})

const pollCloudImport = async ({
  commit, state, hashId, isCurrent,
}) => {
  try {
    const { meta } = await waitForCloudImportReady(hashId, {
      authHeaders: getAuthHeaders(),
      isCancelled: () => !isCurrent(),
      pollIntervalMs: CLOUD_IMPORT_POLL_INTERVAL_MS,
      maxAttempts: CLOUD_IMPORT_POLL_MAX_ATTEMPTS,
    }) || {}
    if (!isCurrent() || !meta) return
    updateCloudImportMetadata(commit, state, { ...meta, import_status: 'success' })
    trackUploadFileSuccessful(cloudImportFile(state), cloudImportExtra(state))
  } catch (error) {
    if (!isCurrent()) return
    Sentry.captureException(error)
    handleCloudImportFailed(commit, state, error.message || 'Cloud import failed. Please try again.')
    trackUploadFileFailed(cloudImportFile(state), error, { ...cloudImportExtra(state), stage: 'poll' })
  }
}

const getBaseTrackingData = (fileInfo) => ({
  eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
  file_name: fileInfo?.file_name,
  file_type: fileInfo?.file_type,
  file_metadata: fileInfo?.file_metadata,
  import_option: fileInfo?.import_option,
  provider: fileInfo?.provider,
  source: fileInfo?.source,
})

const trackImportSuccessful = (fileInfo) => {
  const eventAction = IMPORT_V2_SUCCESSFUL
  const trackData = {
    eventAction,
    ...getBaseTrackingData(fileInfo),
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackImportFailed = (fileInfo, error) => {
  const eventAction = IMPORT_V2_FAILED
  const trackData = {
    eventAction,
    ...getBaseTrackingData(fileInfo),
    error_message: error?.body?.message || error?.message || 'Import failed',
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const _state = {
  isAIChatbotImportModalVisible: false,
  isAIChatbotImportModalVisibleV2: false,
  isUploadingFile: false,
  importingSlidesForPresentationId: null,
  generatingSlidesForPresentationId: null,
  aiChatbotImportSlidesError: '',
  aiChatbotUploadedFile: null,
  sourceFile: null,
  selectedFileName: '',
  selectedImportOption: IMPORT_OPTIONS.IMPORT_ONLY,
  pendingImportFileHashId: null,
  // Sub-status of running imports, straight off each job row's `result`, KEYED
  // BY PRESENTATION. Empty when nothing is importing, and empty for v1, which
  // reports no phases.
  //
  // A map rather than one object because imports outlive the page they were
  // started from: a user can kick off a 100-page deck, navigate away and start
  // another. With a single slot the still-running import kept overwriting the
  // new one's progress, so the toaster on the second deck rendered the first
  // deck's caption count.
  //
  // Entry shape: { presentationId, phase, slides_ready, slides: {created,total},
  // captions: {done,total} }
  importProgressByPresentation: {},
}

const _getters = {
  canUseAIChatbotImportPptxPdf: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-pptx-pdf')
  },
  canUseAIChatbotImportUIUX: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-ui-ux')
  },
  canUseAIChatbotImportGenerateSlides: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-generate-slides')
  },
  canUseNewDocumentSystem: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-AHA-38695-enable-new-document-system')
  },
  canUseDocumentSystemForImport: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-AHA-39870-enable-document-system-for-import')
  },
  canUseImportSheetV2: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-AHA-38470-quiz-import-long-description')
  },
  // AHA-46775: selects the backend import pipeline. v2 converts a page at a time
  // and hands off by signed URL instead of holding the whole deck as base64,
  // which is what OOM-kills import workers on large files.
  // Only affects the async path (canUseDocumentSystemForImport); the legacy
  // synchronous endpoint ignores it.
  canUseImportPipelineV2: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-AHA-46775-import-pipeline-v2')
  },
  canUseAIImportFeature: (_, getters) => {
    return (getters.canUseAIChatbotImportPptxPdf || getters.canUseAIChatbotImportUIUX) &&
      getters.canUseAIChatbotImportGenerateSlides
  },
  isAIChatbotImportModalVisible: (state) => state.isAIChatbotImportModalVisible,
  isAIChatbotImportModalVisibleV2: (state) => state.isAIChatbotImportModalVisibleV2,
  isUploadingFile: (state) => state.isUploadingFile,
  importingSlidesForPresentationId: (state) => state.importingSlidesForPresentationId,
  generatingSlidesForPresentationId: (state) => state.generatingSlidesForPresentationId,

  isCurrentPresentationImportingSlides: (_, __, rootState, rootGetters) => {
    const importingId = rootGetters['aiChatbotImportPptxPdf/importingSlidesForPresentationId']
    const currentId = rootState.presentation.id

    // Only show if we have a valid importing ID and it matches current presentation
    return importingId != null && importingId === currentId
  },

  isCurrentPresentationGeneratingSlides: (_, __, rootState, rootGetters) => {
    const generatingId = rootGetters['aiChatbotImportPptxPdf/generatingSlidesForPresentationId']
    const currentId = rootState.presentation.id

    // Only show if we have a valid generating ID and it matches current presentation
    return generatingId != null && generatingId === currentId
  },

  aiChatbotImportSlidesError: (state) => state.aiChatbotImportSlidesError,
  aiChatbotUploadedFile: (state) => state.aiChatbotUploadedFile,
  sourceFile: (state) => state.sourceFile,
  selectedFileName: (state) => state.selectedFileName,
  isUploadingOrImportingSlides: (state, getters) => getters.isCurrentPresentationImportingSlides ||
    state.isUploadingFile,
  selectedImportOption: (state) => state.selectedImportOption,
  // The progress of the import running on the deck the user is looking at, or
  // null. Consumers all want "is THIS deck importing", so resolving here keeps
  // them from each having to know about the map.
  importProgress: (state, __, rootState) => {
    const currentId = rootState.presentation?.id
    if (currentId == null) return null
    return state.importProgressByPresentation[currentId] || null
  },
  // Every running import, for the few callers that genuinely need the set
  // rather than the current one.
  importProgressByPresentation: (state) => state.importProgressByPresentation,
}

const mutations = {
  // `{ presentationId, progress }` — progress null removes that deck's entry.
  // Replaces the object rather than mutating it so Vue 2 sees the change
  // without Vue.set/Vue.delete.
  SET_IMPORT_PROGRESS(state, { presentationId, progress }) {
    if (presentationId == null) return
    const next = { ...state.importProgressByPresentation }
    if (progress) {
      next[presentationId] = progress
    } else {
      delete next[presentationId]
    }
    // eslint-disable-next-line no-param-reassign
    state.importProgressByPresentation = next
  },
  SET_IS_AI_IMPORT_MODAL_VISIBLE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isAIChatbotImportModalVisible = value
  },
  SET_IS_AI_IMPORT_MODAL_VISIBLE_V2(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isAIChatbotImportModalVisibleV2 = value
  },
  SET_IS_UPLOADING_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isUploadingFile = value
  },
  SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID(state, presentationId) {
    // eslint-disable-next-line no-param-reassign
    state.importingSlidesForPresentationId = presentationId
  },
  SET_GENERATING_SLIDES_FOR_PRESENTATION_ID(state, presentationId) {
    // eslint-disable-next-line no-param-reassign
    state.generatingSlidesForPresentationId = presentationId
  },
  SET_AI_IMPORT_SLIDES_ERROR(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.aiChatbotImportSlidesError = value
  },
  SET_AI_CHATBOT_UPLOADED_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.aiChatbotUploadedFile = value
  },
  SET_SOURCE_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.sourceFile = value
  },
  SET_SELECTED_FILE_NAME(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.selectedFileName = value
  },
  SET_SELECTED_IMPORT_OPTION(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.selectedImportOption = value
  },
  SET_PENDING_IMPORT_FILE_HASH_ID(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.pendingImportFileHashId = value
  },
}

// Progress handler for a v2 import. Extracted from importSlidesAsyncAction to
// keep that action under the cognitive-complexity limit.
//
// Only ever installed when the v2 flag is on. That is stricter than checking
// `result` inside it: on its FINAL tick a v1 job does have a non-null `result`
// — the create_slides response — which would otherwise land in importProgress.
//
// This is what renders slides progressively. The backend suppresses its
// 'presentation: update-slides' broadcast for batched writes (notify=false),
// precisely so the deck is refreshed on OUR schedule rather than once per
// write — 25 writes previously meant 21 uncontrolled reloads. Refreshing when
// the created count moves bounds it to the poll interval instead.
const buildImportProgressHandler = (commit, pullSlides, presentationId) => {
  let lastCreated = 0
  return (result, body) => {
    // Absent until the backend writes its first phase.
    if (!result) return

    // waitForImportJob runs this BEFORE it inspects `status`, so the tick that
    // ends the poll also lands here (importPolling.js). Pulling now would race
    // the unconditional pull that follows the loop: two full-deck fetches in
    // flight at once, and if the older response resolves last it patches the
    // deck back to a stale snapshot, dropping captions. On the FAILED tick it
    // is worse — the pull outlives the error toaster and can put "Your slides
    // are ready!" over it.
    //
    // Nothing is lost by declining: the post-loop pull fetches the same deck a
    // moment later, and it is the one that must run the finished clean-up.
    const isTerminalTick = body?.status === 'success' || body?.status === 'failed'
    if (isTerminalTick) return
    // Stamped with the presentation so a refresh can tell "an import is running
    // on THIS deck" from "an import is running somewhere". importProgress is
    // module state and one import's poll can outlive the user's presence on the
    // deck; without this, navigating away mid-import suppresses slide
    // activation on the next presentation too.
    //
    // Not importingSlidesForPresentationId, which already scopes this way: that
    // one is set for v1 imports as well, and the consumer of this stamp gates
    // behaviour that must stay v2-only. Stamp last so a future backend field
    // cannot shadow it — `result` is merged server-side from arbitrary keys.
    commit('SET_IMPORT_PROGRESS', {
      presentationId,
      progress: { ...result, presentationId },
    })

    const created = result.slides?.created || 0
    if (created > lastCreated) {
      lastCreated = created
      pullSlides()
    }
  }
}

const actions = {
  setIsAIChatbotImportModalVisibleAction({ commit }, value) {
    commit('SET_IS_AI_IMPORT_MODAL_VISIBLE', value)
  },

  setIsAIChatbotImportModalVisibleV2Action({ commit }, value) {
    commit('SET_IS_AI_IMPORT_MODAL_VISIBLE_V2', value)
  },

  setAIChatbotImportSlidesErrorAction({ commit }, value) {
    commit('SET_AI_IMPORT_SLIDES_ERROR', value)
  },

  setIsUploadingFileAction({ commit }, value) {
    commit('SET_IS_UPLOADING_FILE', value)
  },

  setImportingSlidesForPresentationIdAction({ commit }, presentationId) {
    commit('SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID', presentationId)
  },

  // Clear the importing flag ONLY if it still belongs to the import that is
  // finishing. Imports outlive the page they started from, so a long one
  // completing after a second has begun would otherwise null the flag out from
  // under the second — its progress toaster vanishes mid-import and never
  // comes back, because nothing re-sets it.
  clearImportingSlidesForPresentationIdAction({ commit, state }, presentationId) {
    if (state.importingSlidesForPresentationId !== presentationId) return
    commit('SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID', null)
  },

  // Same ownership guard for the generation flag, and it is not redundant: the
  // two track different decks. GENERATE_ONLY sets only this one
  // (OPTIONS_WITH_AI_GENERATION includes it, OPTIONS_WITH_IMPORT does not), so
  // an import on deck A alongside a generate-only on deck B leaves
  // importing=A and generating=B. Clearing this unconditionally when A's import
  // finished stopped B's generation UI and swallowed its ready toast.
  clearGeneratingSlidesForPresentationIdAction({ commit, state }, presentationId) {
    if (state.generatingSlidesForPresentationId !== presentationId) return
    commit('SET_GENERATING_SLIDES_FOR_PRESENTATION_ID', null)
  },

  setGeneratingSlidesForPresentationIdAction({ commit }, presentationId) {
    commit('SET_GENERATING_SLIDES_FOR_PRESENTATION_ID', presentationId)
  },

  setAIChatbotUploadedFileAction({ commit, state }, value) {
    if (state.aiChatbotUploadedFile?.hash_id !== value?.hash_id) {
      commit('SET_PENDING_IMPORT_FILE_HASH_ID', null)
    }
    commit('SET_AI_CHATBOT_UPLOADED_FILE', value)
  },

  resetImportFlowStateAction({ commit }) {
    commit('SET_AI_CHATBOT_UPLOADED_FILE', null)
    commit('SET_SOURCE_FILE', null)
    commit('SET_SELECTED_FILE_NAME', '')
    commit('SET_AI_IMPORT_SLIDES_ERROR', '')
    commit('SET_PENDING_IMPORT_FILE_HASH_ID', null)
  },

  pollCloudImportStatusAction({ commit, state }) {
    const startHashId = state.aiChatbotUploadedFile?.hash_id
    if (!startHashId) return
    pollCloudImport({
      commit,
      state,
      hashId: startHashId,
      isCurrent: () => state.aiChatbotUploadedFile?.hash_id === startHashId,
    })
  },

  setSourceFileAction({ commit }, file) {
    commit('SET_SOURCE_FILE', file)
  },

  setSelectedFileNameAction({ commit }, fileName) {
    commit('SET_SELECTED_FILE_NAME', fileName)
  },

  setSelectedImportOptionAction({ commit }, value) {
    commit('SET_SELECTED_IMPORT_OPTION', value)
  },

  async uploadFileAction({ commit, dispatch, rootGetters }, payload) {
    const file = payload instanceof File ? payload : payload.file
    const skipModal = payload instanceof File ? false : !!payload.skipModal
    try {
      commit('SET_IS_UPLOADING_FILE', true)
      commit('SET_SOURCE_FILE', file) // to re-use this in AIChatbotImportModal.vue

      // Create form data
      const formData = new FormData()
      formData.append('file', file)

      // Use cross-domain HTTP client that bypasses interceptors
      let response = null
      if (rootGetters['aiChatbotImportPptxPdf/canUseNewDocumentSystem']) {
        response = await CrossDomainHttpClient.post(
          externalApis.pptPdfUploadUsingDocumentSystem,
          formData,
          getAuthHeaders(),
        )
      } else {
        response = await CrossDomainHttpClient.post(externalApis.pptPdfUpload, formData, getAuthHeaders())
      }

      commit('SET_AI_CHATBOT_UPLOADED_FILE', response.body)

      if (!skipModal && !rootGetters['aiChatbotImportPptxPdf/isAIChatbotImportModalVisibleV2']) {
        dispatch('setIsAIChatbotImportModalVisibleV2Action', true)
      }
      trackUploadFileSuccessful(file)
    } catch (error) {
      commit('SET_AI_IMPORT_SLIDES_ERROR', 'Could not upload this file. Please try again.')
      trackUploadFileFailed(file, error)
      Sentry.captureException(error, {
        tags: {
          feature: FEATURE_IMPORT_V2,
          context: 'upload-file-action',
        },
      })
    } finally {
      commit('SET_IS_UPLOADING_FILE', false)
    }
  },

  async processToImportSlidesAction({
    state, commit, rootState, rootGetters, dispatch,
  }, payload = {}) {
    try {
      const selectedImportOption = rootGetters['aiChatbotImportPptxPdf/selectedImportOption']
      const fileHashId = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']?.hash_id
      const presentationId = payload.presentationId || rootState.presentation?.id
      const isImport = IMPORT_ACTION_OPTIONS.find((option) => option?.value === selectedImportOption)?.isImport

      if (fileHashId && fileHashId === state.pendingImportFileHashId) return
      if (fileHashId) commit('SET_PENDING_IMPORT_FILE_HASH_ID', fileHashId)

      if (rootState.isShowingSlideSearchPopup) {
        dispatch('setSlideSearchPopupVisibilityAction', false, { root: true })
      }

      if (selectedImportOption === IMPORT_OPTIONS.IMPORT_AND_GENERATE ||
        selectedImportOption === IMPORT_OPTIONS.GENERATE_ONLY) {
        dispatch('aiChatbotImportAndGenerateSlidesAction', {
          importOption: i18nPresenter.t(selectedImportOption),
          isImport,
        })
      } else if (selectedImportOption === IMPORT_OPTIONS.IMPORT_ONLY) {
        dispatch('importSlidesAction', {
          presentationId,
          fileHashId,
        })
      } else {
        // Handle unknown/invalid import options
        throw new Error(`Unknown import option: ${selectedImportOption}`)
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: FEATURE_IMPORT_V2,
          context: 'process-to-import-slides-action',
        },
      })
    }
  },

  async aiChatbotImportAndGenerateSlidesAction({ rootGetters, dispatch }, { importOption, isImport }) {
    try {
      const canUseAIChatbotImportGenerateSlides = rootGetters['aiChatbotImportPptxPdf/canUseAIChatbotImportGenerateSlides']
      if (canUseAIChatbotImportGenerateSlides) {
        if (!rootGetters['rightNav/isAITabActive']) {
          dispatch('rightNav/openAIPanelAction', null, { root: true })
        }

        dispatch('sendImportDataToAIChatbotAction', {
          importOption,
          isImport,
        })
      } else {
        dispatch('docToSlideGenerator/docToSlideUploadFileAction', rootGetters['aiChatbotImportPptxPdf/sourceFile'])
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: FEATURE_IMPORT_V2,
          context: 'ai-chatbot-import-and-generate-slides-action',
        },
      })
    }
  },

  async importSlidesAction({ commit, state, rootGetters }, { presentationId, fileHashId }) {
    try {
      if (!presentationId || !fileHashId) return

      const canUseDocumentSystemForImport = rootGetters['aiChatbotImportPptxPdf/canUseDocumentSystemForImport']

      if (canUseDocumentSystemForImport) {
        // Use async import mechanism with polling
        await this.dispatch('aiChatbotImportPptxPdf/importSlidesAsyncAction', {
          presentationId,
          fileHashId,
        })
      } else {
        // Use synchronous import mechanism
        await CrossDomainHttpClient.post(externalApis.pptPdfImport, {
          presentation_id: presentationId,
          file_hash_id: fileHashId,
        }, getAuthHeaders())
      }
    } catch (error) {
      // Only if this deck is still the one flagged — a second import may have
      // started on another deck while this one was polling, and nulling the
      // flag here would kill its toaster.
      if (state.importingSlidesForPresentationId === presentationId) {
        commit('SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID', null)
      }
      commit('SET_PENDING_IMPORT_FILE_HASH_ID', null)

      // Track import failure
      trackImportFailed({
        file_name: state.aiChatbotUploadedFile?.file_metadata?.original_filename,
        file_type: state.aiChatbotUploadedFile?.file_metadata?.content_type,
        file_metadata: state.aiChatbotUploadedFile?.file_metadata,
        import_option: state.selectedImportOption,
        provider: state.aiChatbotUploadedFile?.import_provider,
        source: state.aiChatbotUploadedFile?.source,
      }, error)

      EventBus.$emit('import: show_import_status_error', { presentationId })
      Sentry.captureException(error, {
        tags: {
          feature: FEATURE_IMPORT_V2,
          context: 'import-slides-action',
        },
      })
    }
  },

  async importSlidesAsyncAction(
    {
      state, commit, dispatch, rootState, rootGetters,
    },
    {
      presentationId,
      fileHashId,
      pollInterval = IMPORT_JOB_POLL_INTERVAL_MS,
      maxPollAttempts = IMPORT_JOB_POLL_MAX_ATTEMPTS,
    },
  ) {
    // Opt-in only: with the flag off the field is omitted entirely and the
    // request is byte-identical to what shipped before this change. 'v2' is the
    // only value the backend accepts — there is no 'v1' to send.
    const wantsPipelineV2 = rootGetters['aiChatbotImportPptxPdf/canUseImportPipelineV2']

    const createResponse = await CrossDomainHttpClient.post(
      externalApis.pptPdfImportAsync,
      {
        presentation_id: presentationId,
        file_hash_id: fileHashId,
        ...(wantsPipelineV2 ? { pipeline_version: 'v2' } : {}),
      },
      getAuthHeaders(),
    )

    const jobId = createResponse.body?.job_id
    if (!jobId) {
      throw new Error('Failed to create import job - no job_id returned')
    }

    // Only meaningful when we asked for v2: the backend echoes what it ran, and
    // silence there means the request was rejected or downgraded. A v2 import
    // that quietly ran the old path would invalidate the comparison this flag
    // exists to make, and nothing else would notice.
    if (wantsPipelineV2 && createResponse.body?.pipeline_version !== 'v2') {
      Sentry.captureMessage(
        `Import pipeline v2 requested but served ${createResponse.body?.pipeline_version || 'nothing'}`,
        {
          level: 'warning',
          tags: { feature: FEATURE_IMPORT_V2, context: 'import-pipeline-version' },
        },
      )
    }

    // Pull the slides the moment the backend says they exist, not when the job
    // finishes. Under the v2 pipeline captioning is a separate phase that runs
    // AFTER the deck is created — on a 100-page PDF that is ~25s to slides_ready
    // against ~165s to 'success'. Waiting for the terminal status would keep the
    // deck invisible for the whole caption phase and throw away the entire win.
    //
    // Re-fetch slides for this presentation: if the editor is mounted they render now,
    // otherwise the pending editor load renders them (presentation.id can still be null here).
    //
    // Safe to call mid-import: handleUIUpdates skips both the viewport change
    // and the import-finished clean-up while importProgress is set.
    const pullSlides = () => OpenAISocketHandler.handleUpdatePresentation(presentationId)

    // Same switch that decides whether we asked for v2 at all, so progressive
    // rendering and the progress readout ship on one flag rather than two.
    const onProgress = wantsPipelineV2
      ? buildImportProgressHandler(commit, pullSlides, presentationId)
      : undefined

    let jobStatus
    try {
      jobStatus = await waitForImportJob(jobId, {
        authHeaders: getAuthHeaders(),
        pollIntervalMs: pollInterval,
        maxAttempts: maxPollAttempts,
        onProgress,
      })
    } finally {
      // ALWAYS clear, including when waitForImportJob throws — it does that on
      // a failed job and on poll timeout, and this action has no catch of its
      // own. importProgress makes handleUIUpdates skip slide activation and the
      // import-finished clean-up, so leaking it would quietly break slide
      // activation for the rest of the session, well beyond this import.
      //
      // It also has to be cleared BEFORE the final pullSlides() below, which is
      // the refresh that must run the clean-up.
      if (wantsPipelineV2) {
        commit('SET_IMPORT_PROGRESS', { presentationId, progress: null })
      }
    }

    trackImportSuccessful({
      file_name: state.aiChatbotUploadedFile?.file_metadata?.original_filename,
      file_type: state.aiChatbotUploadedFile?.file_metadata?.content_type,
      file_metadata: state.aiChatbotUploadedFile?.file_metadata,
      import_option: state.selectedImportOption,
      provider: state.aiChatbotUploadedFile?.import_provider,
      source: state.aiChatbotUploadedFile?.source,
    })

    // The import is genuinely finished now, so this one runs the clean-up:
    // clears the importing flag, hides the progress toaster and shows the
    // success one. For v1 and the cloud-reconcile path — neither of which
    // reports progress — it is also the only pull, exactly as before.
    pullSlides()
    // That reload only clears the importing/generating flags if the editor is mounted,
    // so clear them here when it isn't, to keep the toaster from hanging.
    if (!rootState.presentation?.id) {
      dispatch('clearImportingSlidesForPresentationIdAction', presentationId)
      dispatch('clearGeneratingSlidesForPresentationIdAction', presentationId)
    }

    dispatch('resetImportFlowStateAction')

    return {
      success: true,
      data: jobStatus?.result,
      jobId,
    }
  },

  async sendImportDataToAIChatbotAction({
    rootState, rootGetters, commit, dispatch,
  }, { importOption, isImport }) {
    try {
      commit('SET_IS_AI_IMPORT_MODAL_VISIBLE_V2', false)

      if (rootState.isNewPresentation) {
        dispatch('setIsNewPresentationAction', false, { root: true })
      }

      // AHA2-146: the AI_IMPORT_REQUEST handler lives on the chatbot-app Slide-agent
      // surface (/slides, /slides-v5), NOT on the docked /sandbox bridge iframe. When
      // the tool-bridge flag is ON, the caller (aiChatbotImportAndGenerateSlidesAction)
      // has already opened the AI tab, mounting the native Slide-agent iframe
      // (AIAutoPanel) that getTargetWindow now targets. So DON'T open the /sandbox
      // panel here (it would only pop the wrong, handler-less surface into view), and
      // gate readiness on the Slide-agent iframe's own @load rather than the docked
      // iframe's isIframeReady. Bridge OFF is byte-for-byte unchanged: no docked panel
      // is opened and readiness watches the legacy overlay's isIframeReady as before.
      //
      // The native iframe only mounts (and flips isSlideAgentIframeReady) when BOTH
      // flags are on — shouldRenderNativeSlideAgent = canUsePresenterToolBridge &&
      // canUseAIChatbot (independent Unleash flags). With only the bridge on,
      // getTargetWindow falls back to the docked iframe, so gate on the docked
      // readiness flag too — otherwise this waits the full 20s for a native flag that
      // never flips, then aborts.
      const useNativeSlideAgentIframe =
        rootGetters['aiChatbot/canUsePresenterToolBridge'] &&
        rootGetters['aiChatbot/canUseAIChatbot']
      const readinessGetter = useNativeSlideAgentIframe
        ? 'aiChatbot/isSlideAgentIframeReady'
        : 'aiChatbot/isIframeReady'

      const isIframeReady = await AIChatbotReadinessService.waitForIframeReady(
        rootGetters,
        IFRAME_CONTENT_MAX_WAIT_TIME,
        readinessGetter,
      )

      if (!isIframeReady) {
        throw new Error(
          `Import V2: AI Chatbot iframe failed to become ready within ${IFRAME_CONTENT_MAX_WAIT_TIME}ms. ` +
          'The iframe may not have loaded properly or there may be a network issue.',
        )
      }

      // Add delay to ensure iframe is fully ready
      await utils.sleep(IFRAME_POST_READINESS_DELAY_MS)

      const uploadedFile = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']
      PostMessageSender.sendAIImportRequest({
        importOption,
        isImport,
        file: uploadedFile,
      })
      commit('SET_AI_CHATBOT_UPLOADED_FILE', null)
      commit('SET_SOURCE_FILE', null)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: FEATURE_IMPORT_V2,
          context: 'send-import-data-to-ai-chatbot-action',
        },
      })
    }
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
}

// Export tracking function for use in other modules
export { trackImportSuccessful }
